import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
var API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';
var INACTIVITY_TIMEOUT = 30 * 60 * 1000;
var WARNING_BEFORE = 5 * 60 * 1000;

// ============================================================================
// PIN LOGIN — lockout, device trust, kiosk mode, side-by-side layout
// ============================================================================
function PinLogin({ onLogin, tenant }) {
  var [pin, setPin] = useState('');
  var [code, setCode] = useState('');
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);
  var [lockoutUntil, setLockoutUntil] = useState(null);
  var [lockoutRemaining, setLockoutRemaining] = useState(0);
  var [attempts, setAttempts] = useState(0);
  var MAX_ATTEMPTS = 5;
  var brandColor = tenant.primary_color || '#6c5ce7';

  useEffect(function() {
    var stored = localStorage.getItem('pin_lockout_' + tenant.tenant_id);
    if (stored) {
      var until = parseInt(stored);
      if (until > Date.now()) setLockoutUntil(until);
      else localStorage.removeItem('pin_lockout_' + tenant.tenant_id);
    }
    var storedAttempts = localStorage.getItem('pin_attempts_' + tenant.tenant_id);
    if (storedAttempts) setAttempts(parseInt(storedAttempts));
  }, [tenant.tenant_id]);

  useEffect(function() {
    if (!lockoutUntil) { setLockoutRemaining(0); return; }
    var interval = setInterval(function() {
      var remaining = lockoutUntil - Date.now();
      if (remaining <= 0) {
        setLockoutUntil(null); setLockoutRemaining(0); setAttempts(0);
        localStorage.removeItem('pin_lockout_' + tenant.tenant_id);
        localStorage.removeItem('pin_attempts_' + tenant.tenant_id);
        clearInterval(interval);
      } else { setLockoutRemaining(remaining); }
    }, 1000);
    return function() { clearInterval(interval); };
  }, [lockoutUntil, tenant.tenant_id]);

  useEffect(function() {
    var remembered = localStorage.getItem('trusted_code_' + tenant.tenant_id);
    if (remembered) setCode(remembered);
  }, [tenant.tenant_id]);

  var handleDigit = function(d) { if (pin.length < 6) setPin(pin + d); };
  var handleDelete = function() { setPin(pin.slice(0, -1)); };
  var handleLetterDelete = function() { setCode(code.slice(0, -1)); };
  var handleLetter = function(letter) { if (code.length < 3) setCode(code + letter); };

  var handleSubmit = async function() {
    if (lockoutUntil && lockoutUntil > Date.now()) return;
    if (code.length < 3) { setError('Enter first 3 letters of last name'); return; }
    if (pin.length < 4) { setError('Enter at least 4 digits'); return; }
    setLoading(true); setError('');
    try {
      var res = await fetch(API + '/api/staff-auth/pin-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pin, code: code, tenant_id: tenant.tenant_id })
      });
      var data = await res.json();
      if (data.token) {
        setAttempts(0);
        localStorage.removeItem('pin_attempts_' + tenant.tenant_id);
        localStorage.removeItem('pin_lockout_' + tenant.tenant_id);
        localStorage.setItem('trusted_code_' + tenant.tenant_id, code);
        localStorage.setItem('trusted_business', tenant.tenant_id + ':' + window.location.pathname);
        onLogin(data);
      } else if (data.locked_until) {
        var until = new Date(data.locked_until).getTime();
        setLockoutUntil(until);
        localStorage.setItem('pin_lockout_' + tenant.tenant_id, String(until));
        setError(data.error || 'Too many attempts. Try again later.');
        setPin('');
      } else {
        var newAttempts = attempts + 1;
        setAttempts(newAttempts);
        localStorage.setItem('pin_attempts_' + tenant.tenant_id, String(newAttempts));
        if (newAttempts >= MAX_ATTEMPTS) {
          var lockUntil = Date.now() + 15 * 60 * 1000;
          setLockoutUntil(lockUntil);
          localStorage.setItem('pin_lockout_' + tenant.tenant_id, String(lockUntil));
          setError('Too many failed attempts. Locked for 15 minutes.');
        } else {
          setError((data.error || 'Invalid credentials') + ' (' + (MAX_ATTEMPTS - newAttempts) + ' attempts remaining)');
        }
        setPin('');
      }
    } catch (e) { setError('Cannot connect to server'); }
    setLoading(false);
  };

  var formatLockout = function(ms) {
    var min = Math.floor(ms / 60000);
    var sec = Math.floor((ms % 60000) / 1000);
    return min + ':' + (sec < 10 ? '0' : '') + sec;
  };

  var isLocked = lockoutUntil && lockoutUntil > Date.now();

  var letterBtnStyle = function(disabled) {
    return {
      padding: '12px 0', background: disabled ? '#0a0a15' : '#12121f',
      border: '1px solid #1e1e35', borderRadius: '8px',
      color: disabled ? '#333' : '#fff', fontSize: '15px', fontWeight: '700', cursor: 'pointer',
    };
  };
  var numBtnStyle = {
    padding: '18px', background: '#12121f', border: '1px solid #1e1e35', borderRadius: '14px',
    color: '#fff', fontSize: '22px', fontWeight: '700', cursor: 'pointer',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ maxWidth: '700px', width: '100%', textAlign: 'center' }}>
        {/* Logo / Brand */}
        {tenant.logo_url ? (
          <img src={tenant.logo_url} alt="" style={{ width: '60px', height: '60px', borderRadius: '16px', objectFit: 'cover', marginBottom: '16px' }} />
        ) : (
          <div style={{ width: '60px', height: '60px', borderRadius: '16px', background: 'linear-gradient(135deg, ' + brandColor + ', #a29bfe)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px' }}>{'\u{1F464}'}</span>
          </div>
        )}
        <h1 style={{ color: '#fff', fontSize: '24px', fontWeight: '800', margin: '0 0 6px' }}>{tenant.business_name}</h1>
        <p style={{ color: '#888', fontSize: '14px', margin: '0 0 24px' }}>Staff Portal</p>

        {isLocked ? (
          <div style={{ background: '#d6303130', border: '1px solid #d63031', borderRadius: '12px', padding: '24px', marginBottom: '24px', maxWidth: '360px', margin: '0 auto' }}>
            <p style={{ fontSize: '32px', margin: '0 0 8px' }}>{'\u{1F512}'}</p>
            <p style={{ color: '#ff6b6b', fontWeight: '700', margin: '0 0 4px' }}>Account Locked</p>
            <p style={{ color: '#ff6b6b', fontSize: '13px', margin: '0 0 12px' }}>Too many failed attempts</p>
            <p style={{ color: '#fff', fontSize: '28px', fontWeight: '800', fontFamily: 'monospace', margin: 0 }}>{formatLockout(lockoutRemaining)}</p>
            <p style={{ color: '#888', fontSize: '12px', marginTop: '4px' }}>until unlock</p>
          </div>
        ) : (
          <>
            {error && <div style={{ background: '#d6303120', color: '#ff6b6b', padding: '10px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px' }}>{error}</div>}

            {/* Code display + PIN dots row */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '24px', marginBottom: '24px' }}>
              {/* Code display */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {[0,1,2].map(function(i) {
                  return <div key={i} style={{
                    width: '36px', height: '44px', borderRadius: '8px',
                    background: '#12121f', border: '2px solid ' + (i < code.length ? brandColor : '#2a2a4a'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: '20px', fontWeight: '800',
                  }}>{code[i] || ''}</div>;
                })}
              </div>
              <div style={{ color: '#333', fontSize: '24px' }}>+</div>
              {/* PIN dots */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {[0,1,2,3].map(function(i) {
                  return <div key={i} style={{
                    width: '16px', height: '16px', borderRadius: '50%',
                    background: i < pin.length ? brandColor : '#1e1e35',
                    border: '2px solid ' + (i < pin.length ? brandColor : '#2a2a4a'),
                    transition: 'all 0.15s',
                  }}></div>;
                })}
              </div>
            </div>

            {/* Side-by-side: Letters LEFT, Numbers RIGHT */}
            <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', alignItems: 'flex-start' }}>
              {/* Letter pad */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '6px', width: '260px' }}>
                {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map(function(letter) {
                  return <button key={letter} onClick={function() { handleLetter(letter); }} style={letterBtnStyle(code.length >= 3)}>{letter}</button>;
                })}
                <button onClick={handleLetterDelete} style={{ padding: '12px 0', background: '#12121f', border: '1px solid #1e1e35', borderRadius: '8px', color: '#ff6b6b', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}>{'\u{232B}'}</button>
              </div>

              {/* Number pad */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', width: '220px' }}>
                {[1,2,3,4,5,6,7,8,9].map(function(n) {
                  return <button key={n} onClick={function() { handleDigit(String(n)); }} style={numBtnStyle}>{n}</button>;
                })}
                <button onClick={handleDelete} style={{ padding: '18px', background: '#12121f', border: '1px solid #1e1e35', borderRadius: '14px', color: '#ff6b6b', fontSize: '16px', fontWeight: '700', cursor: 'pointer' }}>{'\u{232B}'}</button>
                <button onClick={function() { handleDigit('0'); }} style={numBtnStyle}>0</button>
                <button onClick={handleSubmit} disabled={loading} style={{
                  padding: '18px', background: 'linear-gradient(135deg, ' + brandColor + ', #a29bfe)', border: 'none', borderRadius: '14px',
                  color: '#fff', fontSize: '16px', fontWeight: '700', cursor: 'pointer', opacity: loading ? 0.6 : 1,
                }}>{loading ? '...' : '\u{2713}'}</button>
              </div>
            </div>

            {/* Attempt indicator */}
            {attempts > 0 && attempts < MAX_ATTEMPTS && (
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '6px' }}>
                {Array.from({ length: MAX_ATTEMPTS }).map(function(_, i) {
                  return <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: i < attempts ? '#d63031' : '#1e1e35' }}></div>;
                })}
              </div>
            )}
          </>
        )}
        {/* Kiosk mode */}
        <button
          onClick={function() {
            var el = document.documentElement;
            if (el.requestFullscreen) el.requestFullscreen();
            else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
          }}
          style={{ marginTop: '32px', background: 'none', border: 'none', color: '#333', fontSize: '12px', cursor: 'pointer', padding: '8px' }}
        >{'\u{26F6}'} Kiosk Mode</button>
      </div>
    </div>
  );
}

// ============================================================================
// STAFF DASHBOARD
// ============================================================================
function StaffDashboard({ staff, token, onLogout }) {
  var [tab, setTab] = useState('today');
  var [schedule, setSchedule] = useState([]);
  var [weekSchedule, setWeekSchedule] = useState([]);
  var [clockedIn, setClockedIn] = useState(false);
  var [clockTime, setClockTime] = useState(null);
  var [stats, setStats] = useState({ bookingsToday: 0, bookingsWeek: 0, earnings: 0 });
  var [loading, setLoading] = useState(true);
  var [showTimeout, setShowTimeout] = useState(false);
  var timeoutRef = useRef(null);
  var warningRef = useRef(null);
  var headers = { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token };

  var resetTimer = useCallback(function() {
    setShowTimeout(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    warningRef.current = setTimeout(function() { setShowTimeout(true); }, INACTIVITY_TIMEOUT - WARNING_BEFORE);
    timeoutRef.current = setTimeout(function() { onLogout(); }, INACTIVITY_TIMEOUT);
  }, [onLogout]);

  useEffect(function() {
    var events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    var handler = function() { resetTimer(); };
    events.forEach(function(e) { document.addEventListener(e, handler); });
    resetTimer();
    return function() {
      events.forEach(function(e) { document.removeEventListener(e, handler); });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [resetTimer]);

  var loadData = useCallback(async function() {
    setLoading(true);
    try {
      var [schedRes, weekRes, statsRes] = await Promise.all([
        fetch(API + '/api/staff-auth/my-schedule?range=today', { headers: headers }),
        fetch(API + '/api/staff-auth/my-schedule?range=week', { headers: headers }),
        fetch(API + '/api/staff-auth/my-stats', { headers: headers })
      ]);
      if (schedRes.ok) { var s = await schedRes.json(); setSchedule(s.schedule || s || []); }
      if (weekRes.ok) { var w = await weekRes.json(); setWeekSchedule(w.schedule || w || []); }
      if (statsRes.ok) { var st = await statsRes.json(); setStats(st); }
    } catch (e) { console.error('Load error:', e); }
    setLoading(false);
  }, [token]);

  useEffect(function() { loadData(); }, [loadData]);

  var handleClockIn = async function() {
    try {
      var res = await fetch(API + '/api/staff-auth/clock-in', { method: 'POST', headers: headers });
      if (res.ok) { setClockedIn(true); setClockTime(new Date().toLocaleTimeString()); }
    } catch (e) { console.error('Clock in error:', e); }
  };
  var handleClockOut = async function() {
    try {
      var res = await fetch(API + '/api/staff-auth/clock-out', { method: 'POST', headers: headers });
      if (res.ok) { setClockedIn(false); setClockTime(null); }
    } catch (e) { console.error('Clock out error:', e); }
  };
  var formatTime = function(t) {
    if (!t) return '';
    return new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };
  var displayName = staff.name || ((staff.first_name || '') + ' ' + (staff.last_name || '')).trim() || 'Staff';

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a1a', color: '#fff' }}>
      {showTimeout && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ textAlign: 'center', padding: '32px' }}>
            <p style={{ fontSize: '48px', margin: '0 0 16px' }}>{'\u{23F0}'}</p>
            <h2 style={{ color: '#fff', margin: '0 0 8px' }}>Still there?</h2>
            <p style={{ color: '#888', margin: '0 0 24px' }}>You will be signed out in 5 minutes due to inactivity</p>
            <button onClick={function() { resetTimer(); }} style={{ padding: '14px 48px', background: 'linear-gradient(135deg, #6c5ce7, #a29bfe)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '16px', fontWeight: '700', cursor: 'pointer' }}>
              I am still here
            </button>
          </div>
        </div>
      )}

      <div style={{ background: '#12121f', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1e1e35' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700' }}>Welcome, {displayName}</h2>
          <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <button onClick={onLogout} style={{ background: 'none', border: '1px solid #333', borderRadius: '8px', color: '#888', padding: '8px 16px', cursor: 'pointer', fontSize: '13px' }}>Sign Out</button>
      </div>

      <div style={{ padding: '24px', textAlign: 'center' }}>
        {!clockedIn ? (
          <button onClick={handleClockIn} style={{ padding: '16px 48px', background: 'linear-gradient(135deg, #00b894, #00cec9)', border: 'none', borderRadius: '14px', color: '#fff', fontSize: '18px', fontWeight: '700', cursor: 'pointer', width: '100%', maxWidth: '320px' }}>
            {'\u{1F7E2}'} Clock In
          </button>
        ) : (
          <div>
            <p style={{ color: '#00b894', marginBottom: '8px', fontSize: '14px' }}>{'\u{2705}'} Clocked in at {clockTime}</p>
            <button onClick={handleClockOut} style={{ padding: '16px 48px', background: 'linear-gradient(135deg, #d63031, #e17055)', border: 'none', borderRadius: '14px', color: '#fff', fontSize: '18px', fontWeight: '700', cursor: 'pointer', width: '100%', maxWidth: '320px' }}>
              {'\u{1F534}'} Clock Out
            </button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid #1e1e35', padding: '0 24px' }}>
        {['today', 'week', 'stats'].map(function(t) {
          return <button key={t} onClick={function() { setTab(t); }} style={{
            padding: '12px 24px', background: 'none', border: 'none', borderBottom: tab === t ? '2px solid #6c5ce7' : '2px solid transparent',
            color: tab === t ? '#6c5ce7' : '#888', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
          }}>{t === 'today' ? "Today's Schedule" : t === 'week' ? 'This Week' : 'My Stats'}</button>;
        })}
      </div>

      <div style={{ padding: '24px' }}>
        {loading ? (
          <p style={{ color: '#888', textAlign: 'center' }}>Loading...</p>
        ) : tab === 'today' ? (
          <div>
            {schedule.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center' }}>No appointments today</p>
            ) : schedule.map(function(apt, i) {
              return <div key={i} style={{ background: '#12121f', borderRadius: '12px', padding: '16px', marginBottom: '12px', border: '1px solid #1e1e35' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: '700' }}>{apt.customer_name || apt.contact_name || 'Walk-in'}</p>
                    <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>{apt.service_name || apt.service || 'Service'}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, color: '#6c5ce7', fontWeight: '700' }}>{formatTime(apt.booking_time || apt.start_time || apt.time)}</p>
                    <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>{apt.duration || 60} min</p>
                  </div>
                </div>
              </div>;
            })}
          </div>
        ) : tab === 'week' ? (
          <div>
            {weekSchedule.length === 0 ? (
              <p style={{ color: '#888', textAlign: 'center' }}>No appointments this week</p>
            ) : weekSchedule.map(function(apt, i) {
              return <div key={i} style={{ background: '#12121f', borderRadius: '12px', padding: '16px', marginBottom: '12px', border: '1px solid #1e1e35' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: '700' }}>{apt.customer_name || apt.contact_name || 'Walk-in'}</p>
                    <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>{apt.service_name || apt.service || 'Service'}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ margin: 0, color: '#6c5ce7', fontWeight: '700' }}>{formatTime(apt.booking_time || apt.start_time || apt.time)}</p>
                    <p style={{ margin: '4px 0 0', color: '#888', fontSize: '13px' }}>{apt.booking_date || ''}</p>
                  </div>
                </div>
              </div>;
            })}
          </div>
        ) : (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
              <div style={{ background: '#12121f', borderRadius: '12px', padding: '20px', border: '1px solid #1e1e35', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>Today</p>
                <p style={{ margin: '8px 0 0', fontSize: '28px', fontWeight: '800', color: '#6c5ce7' }}>{stats.bookingsToday || 0}</p>
                <p style={{ margin: 0, color: '#888', fontSize: '12px' }}>bookings</p>
              </div>
              <div style={{ background: '#12121f', borderRadius: '12px', padding: '20px', border: '1px solid #1e1e35', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>This Week</p>
                <p style={{ margin: '8px 0 0', fontSize: '28px', fontWeight: '800', color: '#00b894' }}>{stats.bookingsWeek || 0}</p>
                <p style={{ margin: 0, color: '#888', fontSize: '12px' }}>bookings</p>
              </div>
              <div style={{ background: '#12121f', borderRadius: '12px', padding: '20px', border: '1px solid #1e1e35', textAlign: 'center', gridColumn: 'span 2' }}>
                <p style={{ margin: 0, color: '#888', fontSize: '13px' }}>Earnings</p>
                <p style={{ margin: '8px 0 0', fontSize: '28px', fontWeight: '800', color: '#00cec9' }}>${parseFloat(stats.earnings || 0).toFixed(2)}</p>
                <p style={{ margin: 0, color: '#888', fontSize: '12px' }}>commission this period</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center', padding: '16px' }}>
        <button onClick={function() { if (document.exitFullscreen) document.exitFullscreen(); else if (document.webkitExitFullscreen) document.webkitExitFullscreen(); }}
          style={{ background: 'none', border: 'none', color: '#111', fontSize: '11px', cursor: 'pointer' }}>Exit Kiosk</button>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN
// ============================================================================
function EmployeePortal() {
  var { subdomain } = useParams();
  var [staff, setStaff] = useState(null);
  var [token, setToken] = useState(localStorage.getItem('employee_token_' + (window.location.pathname.split('/')[2] || 'default')));
  var [tenant, setTenant] = useState(null);
  var [tenantError, setTenantError] = useState(null);

  useEffect(function() {
    if (!subdomain) {
      var trusted = localStorage.getItem('trusted_business');
      if (trusted) {
        var path = trusted.split(':').slice(1).join(':');
        if (path && path !== window.location.pathname) window.location.href = path;
      }
    }
  }, [subdomain]);

  useEffect(function() {
    if (subdomain) {
      fetch(API + '/api/staff-auth/resolve/' + subdomain)
        .then(function(r) { return r.json(); })
        .then(function(d) { if (d.tenant_id) setTenant(d); else setTenantError('Business not found'); })
        .catch(function() { setTenantError('Cannot connect to server'); });
    }
  }, [subdomain]);

  useEffect(function() {
    if (token) {
      fetch(API + '/api/staff-auth/me', { headers: { 'Authorization': 'Bearer ' + token } })
        .then(function(res) { if (res.ok) return res.json(); throw new Error('Invalid'); })
        .then(function(data) { setStaff(data.staff || data); })
        .catch(function() { localStorage.removeItem('employee_token_' + (subdomain || 'default')); setToken(null); });
    }
  }, [token, subdomain]);

  var handleLogin = function(data) {
    setStaff(data.user || data.staff);
    setToken(data.token);
    localStorage.setItem('employee_token_' + (subdomain || 'default'), data.token);
  };
  var handleLogout = function() {
    setStaff(null); setToken(null);
    localStorage.removeItem('employee_token_' + (subdomain || 'default'));
  };

  useEffect(function() {
    var handler = function() {
      if (!localStorage.getItem('employee_token_' + (window.location.pathname.split('/')[2] || 'default'))) {
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.addEventListener('popstate', handler);
    return function() { window.removeEventListener('popstate', handler); };
  }, []);

  if (!subdomain) return (
    <div style={{ minHeight: '100vh', background: '#0a0a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ textAlign: 'center', color: '#888' }}>
        <p style={{ fontSize: '48px', margin: '0 0 16px' }}>{'\u{1F464}'}</p>
        <h2 style={{ color: '#fff', margin: '0 0 8px' }}>Employee Portal</h2>
        <p>Ask your manager for your employee portal link.</p>
      </div>
    </div>
  );
  if (tenantError) return (<div style={{ minHeight: '100vh', background: '#0a0a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ff6b6b' }}>{tenantError}</div>);
  if (!tenant) return (<div style={{ minHeight: '100vh', background: '#0a0a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>Loading...</div>);
  if (!token || !staff) return <PinLogin onLogin={handleLogin} tenant={tenant} />;
  return <StaffDashboard staff={staff} token={token} onLogout={handleLogout} />;
}

export default EmployeePortal;
